/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * SECTOR INHERENT RISK — the single source of truth for the entity-type
 * vocabulary and its inherent ratings.
 *
 * Moved here from api/_risk-methodology.js (task_a7e72816) so the officer
 * dashboard can offer the SAME list in the "Correct entity type" control that
 * the engine rates against: the client bundle (react-scripts) may only import
 * from src/, while api/ imports freely from src/shared, so this is the one
 * location both can read. _risk-methodology.js re-exports these unchanged.
 *
 * Anchored to the sectors identified in the UK National Risk Assessment, the EU
 * Supranational Risk Assessment, and the FATF DNFBP designations, so a rating is
 * attributable to a published assessment.
 *
 * Inherent means BEFORE mitigation. Licensing and supervision do not move a
 * sector rating; they are recorded in the customer category. A licensed EMI is
 * High here and Low there, and lands at Medium overall. That separation is the
 * single most important design decision in the methodology.
 */
export const SECTOR_RISK = {
  // Low inherent
  SUPERVISED_FI: 'Low',
  LISTED_COMPANY: 'Low',
  PUBLIC_AUTHORITY: 'Low',
  PENSION_SCHEME: 'Low',
  TRADING_COMPANY: 'Low',
  MANUFACTURING: 'Low',
  TECHNOLOGY: 'Low',
  HEALTHCARE_EDUCATION: 'Low',
  TRANSPORT_LOGISTICS: 'Low',

  // Medium inherent
  REGULATED_FUND: 'Medium',
  ASSET_MANAGER: 'Medium',
  SPOT_BROKER: 'Medium',
  CFD_BROKER: 'Medium',
  PROP_FIRM: 'Medium',
  PROP_TRADING: 'Medium',
  FAMILY_OFFICE: 'Medium',
  CORPORATE_TREASURY: 'Medium',
  REAL_ESTATE: 'Medium',
  CONSTRUCTION: 'Medium',
  IMPORT_EXPORT: 'Medium',
  HOSPITALITY_RETAIL: 'Medium',
  ECOMMERCE: 'Medium',
  PHARMACEUTICALS: 'Medium',
  RECRUITMENT: 'Medium',
  MOTOR_TRADE: 'Medium',
  TRAVEL: 'Medium',
  OTHER: 'Medium',

  // High inherent
  MSB: 'High',
  VASP: 'High',
  GAMING: 'High',
  TCSP: 'High',
  HIGH_VALUE_DEALER: 'High',
  CASH_INTENSIVE: 'High',
  ARMS_DEFENCE: 'High',
  EXTRACTIVES: 'High',
  COMMODITY_TRADING: 'High',
  ADULT_ENTERTAINMENT: 'High',
  UNREGULATED_SCHEME: 'High',
  NPO_HIGH_RISK: 'High',
  SHELL_HOLDING: 'High',
  // CID-RM-1.4: Medium to High. The UK National Risk Assessment of Money
  // Laundering and Terrorist Financing 2025 (HM Treasury and Home Office, July
  // 2025), section 5, rates legal service providers and accountancy service
  // providers High for money laundering. A case decided under 1.3 or earlier
  // keeps Medium (rateSector).
  LEGAL_ACCOUNTING: 'High'
};

/** Vocabulary emitted to the model. Kept with the table so prompts cannot drift. */
export const ENTITY_TYPES = Object.keys(SECTOR_RISK);
export const ENTITY_TYPE_PROMPT = ENTITY_TYPES.join(' | ');
